import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { API_URL } from '../services/api';

const TenantContext = createContext(null);

export const TenantProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState({
    tenantPrefix: 'default',
    tenantName: 'FMS Customer Portal',
    loginLogo: '/bannernew.png',
    navLogo: '/globe2.png',
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    isDefault: true,
    loading: true
  });

  const extractTenantPrefix = () => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    
    // Check for subdomain pattern (e.g., tenant1.portal.myfmsweb.com)
    const subdomainMatch = hostname.match(/^([^.]+)\./);
    if (subdomainMatch && subdomainMatch[1] !== 'www' && subdomainMatch[1] !== 'portal') {
      return subdomainMatch[1];
    }
    
    // Check for path prefix pattern (e.g., /tenant1/login)
    const pathMatch = pathname.match(/^\/([^\/]+)/);
    if (pathMatch && pathMatch[1] !== 'login' && pathMatch[1] !== 'inventory' && 
        pathMatch[1] !== 'orders' && pathMatch[1] !== 'bols' && 
        pathMatch[1] !== 'warehouse-receipts' && pathMatch[1] !== 'catalog' && 
        pathMatch[1] !== 'accounts' && pathMatch[1] !== 'rate-calculator') {
      return pathMatch[1];
    }
    
    return null;
  };

  const loadTenantConfig = async () => {
    try {
      const tenantPrefix = extractTenantPrefix();

      if (!tenantPrefix) {
        setTenantConfig(prev => ({ ...prev, loading: false }));
        return;
      }

      const response = await api.get(`/api/tenants/config/${tenantPrefix}`);
      const data = response.data;
      
      setTenantConfig({
        ...data,
        loading: false
      });

      // Apply CSS custom properties for theming
      if (response.data.primaryColor) {
        document.documentElement.style.setProperty('--tenant-primary-color', response.data.primaryColor);
      }
      if (response.data.secondaryColor) {
        document.documentElement.style.setProperty('--tenant-secondary-color', response.data.secondaryColor);
      }

      // Update page title
      if (response.data.tenantName) {
        document.title = response.data.tenantName;
      }

    } catch (error) {
      // Fall back to default configuration
      setTenantConfig(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    loadTenantConfig();
  }, []);

  // Listen for route changes to detect tenant prefix changes
  useEffect(() => {
    const handleLocationChange = () => {
      loadTenantConfig();
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  return (
    <TenantContext.Provider value={{ 
      tenantConfig, 
      loadTenantConfig,
      extractTenantPrefix 
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext; 